import React, { useState } from 'react';
import { Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const css = `
  .contact-page { background: #0d0d0d; }
  .contact-grid {
    max-width: 1180px; margin: 0 auto; padding: 80px 24px;
    display: grid; grid-template-columns: 1fr 1.5fr; gap: 64px; align-items: start;
  }
  .contact-card {
    background: #161616; border: 1px solid rgba(255,255,255,0.07);
    border-top: 3px solid #c0392b; padding: 36px;
  }
  .contact-card h3 { font-size: 1.25rem; text-transform: uppercase; color: #fff; letter-spacing: 0.04em; margin-bottom: 8px; }
  .contact-card > p { font-size: 0.88rem; color: #888; margin-bottom: 28px; line-height: 1.65; }
  .ci { display: flex; gap: 14px; align-items: flex-start; padding: 18px 0; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ci:last-of-type { border-bottom: none; }
  .ci-icon { width: 40px; height: 40px; background: rgba(192,57,43,0.1); display: flex; align-items: center; justify-content: center; color: #c0392b; flex-shrink: 0; }
  .ci strong { font-family: 'Oswald', sans-serif; font-size: 0.72rem; letter-spacing: 0.18em; text-transform: uppercase; color: #d4a017; display: block; margin-bottom: 5px; }
  .ci span, .ci a { font-size: 0.97rem; color: #bbb; text-decoration: none; transition: color 0.2s; }
  .ci a:hover { color: #fff; }
  .ci small { display: block; font-size: 0.8rem; color: #666; margin-top: 3px; }

  .form-wrap h2 { font-size: clamp(1.8rem,3.5vw,2.5rem); text-transform: uppercase; color: #fff; margin-bottom: 8px; }
  .form-wrap > p { color: #888; margin-bottom: 32px; font-size: 0.93rem; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .field label { font-family: 'Oswald', sans-serif; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: #777; }
  .field input, .field textarea, .field select {
    background: #1a1a1a; border: 1px solid rgba(255,255,255,0.1); color: #fff;
    padding: 12px 15px; font-family: 'Open Sans', sans-serif; font-size: 0.93rem;
    outline: none; transition: border-color 0.2s; width: 100%;
    -webkit-appearance: none; appearance: none; border-radius: 0;
  }
  .field input::placeholder, .field textarea::placeholder { color: #4a4a4a; }
  .field input:focus, .field textarea:focus, .field select:focus { border-color: #c0392b; }
  .field select { cursor: pointer; }
  .field select option { background: #1a1a1a; }
  .field textarea { resize: vertical; min-height: 118px; }
  .form-submit {
    display: inline-flex; align-items: center; gap: 10px;
    background: #c0392b; color: #fff; padding: 14px 36px;
    font-family: 'Oswald', sans-serif; font-size: 0.93rem; font-weight: 600;
    letter-spacing: 0.12em; text-transform: uppercase; border: none; cursor: pointer;
    transition: background 0.2s, transform 0.15s; margin-top: 6px;
  }
  .form-submit:hover { background: #96281b; transform: translateY(-2px); }

  .success-box {
    background: #1a1a1a; border: 1px solid rgba(34,197,94,0.3);
    border-top: 3px solid #22c55e; padding: 48px; text-align: center;
  }
  .success-box svg { color: #22c55e; margin: 0 auto 16px; display: block; }
  .success-box h3 { font-size: 1.4rem; text-transform: uppercase; color: #fff; margin-bottom: 10px; }
  .success-box p { color: #888; font-size: 0.93rem; }

  @media (max-width:900px) { .contact-grid { grid-template-columns: 1fr; gap: 44px; } .form-row { grid-template-columns: 1fr; } }
`;

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: '', message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <style>{css}</style>
      <div className="page-header">
        <div className="container">
          <span className="section-label">Reach Out</span>
          <h1>Contact Us</h1>
          <p>Have a question, a build idea, or a part you can't find? We want to hear from you.</p>
        </div>
      </div>
      <div className="contact-page">
        <div className="contact-grid">
          <div className="contact-card">
            <h3>Get in Touch</h3>
            <p>The easiest way to reach us is a phone call. We'll give you a straight answer and no runaround.</p>
            <div className="ci">
              <div className="ci-icon"><Phone size={17} /></div>
              <div><strong>Phone</strong><a href="tel:7172785897"><span>(717) 278-5897</span></a><small>Best way to reach us</small></div>
            </div>
            <div className="ci">
              <div className="ci-icon"><MapPin size={17} /></div>
              <div><strong>Location</strong><span>New Holland, PA</span><small>Lancaster County, Pennsylvania</small></div>
            </div>
            <div className="ci">
              <div className="ci-icon"><Clock size={17} /></div>
              <div><strong>Hours</strong><span>Call ahead to confirm</span><small>We work by appointment &amp; availability</small></div>
            </div>
          </div>

          <div className="form-wrap">
            <span className="section-label">Send a Message</span>
            <h2>What Can We Help With?</h2>
            <p>Fill out the form and we'll get back to you soon. For a faster response, give us a call.</p>
            {submitted ? (
              <div className="success-box">
                <CheckCircle size={48} />
                <h3>Message Received</h3>
                <p>Thanks for reaching out. We'll be in touch shortly. Or call us at (717) 278-5897 for a faster response.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="field">
                    <label htmlFor="name">Your Name</label>
                    <input id="name" name="name" type="text" placeholder="John Doe" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="field">
                    <label htmlFor="phone">Phone Number</label>
                    <input id="phone" name="phone" type="tel" placeholder="(717) 000-0000" value={form.phone} onChange={handleChange} />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="email">Email Address</label>
                  <input id="email" name="email" type="email" placeholder="you@email.com" value={form.email} onChange={handleChange} />
                </div>
                <div className="field">
                  <label htmlFor="service">What Do You Need?</label>
                  <select id="service" name="service" value={form.service} onChange={handleChange} required>
                    <option value="">Select a service...</option>
                    <option value="repair">V-Twin Repair</option>
                    <option value="custom">Custom Build</option>
                    <option value="performance">Performance Work</option>
                    <option value="parts">Parts Sourcing</option>
                    <option value="other">Other / General Question</option>
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="message">Tell Us More</label>
                  <textarea id="message" name="message" placeholder="Describe your bike, the issue, or what you're looking for..." value={form.message} onChange={handleChange} required />
                </div>
                <button type="submit" className="form-submit"><Send size={16} /> Send Message</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
